import { useEffect, useState } from "react";
import Header from "./Header";
import { addTicket, fetchTickets, updateTicket } from "./service";
import { CoreContainer, Notification } from "./style";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { ProjectButton } from "../project/style";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

const Support = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [orderIdentifier, setOrderIdentifier] = useState(null);
  const [subject, setSubject] = useState("");
  const [eta, setEta] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [etaFilter, setEtaFilter] = useState("all");
  const [sortFilter, setSortFilter] = useState("oldest");
  const [today, setToday] = useState("");
  const [ticketModal, setTicketModal] = useState(false);
  const [ticketModalInfo, setTicketModalInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const statusFilterKeyMapper = {
    all: ["open", "closed"],
    open: ["open"],
    closed: ["closed"],
  };

  useEffect(() => {
    getTickets();
    setToday(new Date().toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    let filterTickets = tickets.filter((ticket) =>
      statusFilterKeyMapper[statusFilter].includes(ticket.status)
    );

    filterTickets = filterTickets.filter((ticket) => {
      switch (etaFilter) {
        case "all":
          return true;
        case "set":
          return ticket.eta;
        case "not_set":
          return !ticket.eta;
        default:
          return false;
      }
    });

    if (sortFilter === "oldest") filterTickets.sort((a, b) => a.id - b.id);
    else if (sortFilter === "latest") filterTickets.sort((a, b) => b.id - a.id);
    else if (sortFilter === "eta")
      filterTickets.sort((a, b) => new Date(a.eta) - new Date(b.eta));

    setFilteredTickets(filterTickets);
  }, [tickets, statusFilter, etaFilter, sortFilter]);

  const getTickets = () => {
    setIsLoading(true);

    fetchTickets()
      .then(({ tickets, orders }) => {
        setTickets(tickets);
        setOrders(orders);
      })
      .catch((err) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: err.error });
      })
      .finally(() => setIsLoading(false));
  };

  const createTicket = ({ orderIdentifier, subject }) => {
    if (orderIdentifier && subject) {
      setIsLoading(true);

      addTicket({ orderIdentifier, subject })
        .then(({ ticket }) => {
          setTickets((prevTickets) => [...prevTickets, ticket]);
          closeTicketModal();
        })
        .catch((err) => {
          setIsNotificationVisible(true);
          setNotification({ type: "error", message: err.error });
        })
        .finally(() => setIsLoading(false));
    } else alert("Please fill the details");
  };

  const editTicket = ({ id, key, value }) => {
    if (id && key && value) {
      setIsLoading(true);

      updateTicket({ id, key, value })
        .then(({ ticket }) => {
          const newTickets = tickets;
          const oldTicket = newTickets.find((e) => e.id == ticket.id);
          Object.assign(oldTicket, ticket);
          setTickets(newTickets);

          closeTicketModal();
        })
        .catch((err) => {
          setIsNotificationVisible(true);
          setNotification({ type: "error", message: err.error });
        })
        .finally(() => setIsLoading(false));
    } else alert("Please fill the details");
  };

  const closeTicketModal = () => {
    setTicketModal(false);
    setTicketModalInfo(null);
    setOrderIdentifier(null);
    setSubject("");
    setEta("");
  };

  const formatDate = (str) => {
    const date = new Date(str);

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const assignEta = (str) => str && setEta(str.split("T")[0]);

  const getColorForEta = (ticket) => {
    if (ticket.status === "closed") return "subtle-gray";
    else {
      if (ticket.eta && new Date(ticket.eta) < new Date()) return "text-danger";
      else if (!ticket.eta) return "text-warning";
      else return "";
    }
  };

  return (
    <>
      <Header nav={true} />
      {isNotificationVisible && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={() => setIsNotificationVisible(false)}
        >
          {notification.message}
        </Notification>
      )}
      {isLoading ? (
        <CoreContainer height="70vh">
          <Spinner size="sm" style={{ margin: "auto" }}>
            Loading...
          </Spinner>
        </CoreContainer>
      ) : (
        <>
          <div className="px-5 py-4">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex column-gap-3">
                <div>
                  <Label
                    className="text-secondary fw-bold mb-1"
                    for="status_select"
                  >
                    Status
                  </Label>
                  <Input
                    type="select"
                    id="status_select"
                    className="fs-12px"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </Input>
                </div>
                <div>
                  <Label
                    className="text-secondary fw-bold mb-1"
                    for="eta_select"
                  >
                    ETA
                  </Label>
                  <Input
                    type="select"
                    id="eta_select"
                    className="fs-12px"
                    value={etaFilter}
                    onChange={(e) => setEtaFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="set">Set</option>
                    <option value="not_set">Not set</option>
                  </Input>
                </div>
                <div>
                  <Label className="text-secondary fw-bold mb-1" for="sort">
                    Sort
                  </Label>
                  <Input
                    type="select"
                    id="sort"
                    className="fs-12px"
                    value={sortFilter}
                    onChange={(e) => setSortFilter(e.target.value)}
                  >
                    <option value="oldest">Oldest first</option>
                    <option value="latest">Latest first</option>
                    <option value="eta">ETA</option>
                  </Input>
                </div>
              </div>
              <ProjectButton
                onClick={() => {
                  setTicketModal(true);
                  setTicketModalInfo({
                    action: "create",
                  });
                }}
                className="rounded-1"
              >
                Create Ticket
              </ProjectButton>
            </div>
            <Table bordered hover className="mt-4">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Org</th>
                  <th>Project</th>
                  <th>Subject</th>
                  <th>ETA</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody className="text-capitalize">
                {filteredTickets.map((ticket, index) => (
                  <tr key={index}>
                    <td>{ticket.identifier}</td>
                    <td>{ticket.info.org}</td>
                    <td>{ticket.info.project}</td>
                    <td>{ticket.subject}</td>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className={`${getColorForEta(ticket)}`}>
                          {(ticket.eta && formatDate(ticket.eta)) || "Not set"}
                        </span>
                        {ticket.status !== "closed" && (
                          <i
                            className="fa-solid fa-pen pointer"
                            onClick={() => {
                              assignEta(ticket.eta);
                              setTicketModal(true);
                              setTicketModalInfo({
                                action: "edit",
                                key: "eta",
                                id: ticket.id,
                              });
                            }}
                          />
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex justify-content-between align-items-center">
                        {ticket.status}{" "}
                        {ticket.status === "closed" && (
                          <span className="subtle-gray">
                            {formatDate(ticket.closed_on)}
                          </span>
                        )}
                        {ticket.status !== "closed" && (
                          <a
                            className="link-underline-primary pointer"
                            onClick={() => {
                              setTicketModal(true);
                              setTicketModalInfo({
                                action: "edit",
                                key: "status",
                                id: ticket.id,
                              });
                            }}
                          >
                            Close Ticket
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {filteredTickets.length === 0 && (
              <h5 className="mt-4 text-center">No Tickets Found</h5>
            )}
          </div>
        </>
      )}

      <Modal isOpen={ticketModal} toggle={closeTicketModal}>
        {ticketModalInfo && (
          <>
            <ModalHeader toggle={closeTicketModal}>
              {ticketModalInfo.action === "create"
                ? "Create Ticket"
                : ticketModalInfo.key === "eta"
                  ? "ETA Update"
                  : "Close Ticket"}
            </ModalHeader>
            <ModalBody className="d-flex flex-column row-gap-3">
              {ticketModalInfo.action === "create" ? (
                <>
                  <div>
                    <p className="m-0 text-secondary fw-bold">
                      Order Identifier
                    </p>
                    <Select
                      className="mt-1"
                      options={orders.map((order) => {
                        const label = order.context
                          ? `(${order.context[0].toUpperCase()})`
                          : "";

                        return {
                          label: `${order.org_name} / ${order.project_name} - ${order.identifier} ${label}`,
                          value: order.identifier,
                        };
                      })}
                      onChange={(e) => setOrderIdentifier(e.value)}
                    />
                  </div>
                  <div>
                    <p className="m-0 text-secondary fw-bold">Subject</p>
                    <Input
                      type="text"
                      id="subject"
                      className="mt-1 py-2"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>{" "}
                </>
              ) : ticketModalInfo.key === "eta" ? (
                <div>
                  <p className="m-0 text-secondary fw-bold">ETA</p>
                  <Input
                    type="date"
                    id="date"
                    className="mt-1 py-2"
                    value={eta}
                    onChange={(e) => setEta(e.target.value)}
                    min={today}
                  />
                </div>
              ) : (
                <p className="m-0">
                  Are you sure you want to close the ticket?
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                onClick={() => {
                  if (ticketModalInfo.action === "create")
                    createTicket({ orderIdentifier, subject });
                  else if (ticketModalInfo.key === "eta")
                    editTicket({
                      id: ticketModalInfo.id,
                      key: ticketModalInfo.key,
                      value: eta,
                    });
                  else if (ticketModalInfo.key === "status")
                    editTicket({
                      id: ticketModalInfo.id,
                      key: ticketModalInfo.key,
                      value: "closed",
                    });
                }}
              >
                Submit
              </Button>
              <Button color="secondary" onClick={closeTicketModal}>
                Cancel
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </>
  );
};

export default Support;
